// src/pages/Home.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import games, { ACTIVE_GAME_SOURCE_NAME } from "../data/games";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getFavoritesForUser, toggleFavoriteForUser } from "../utils/favorites";
import { getPopularityMap } from "../utils/gamePopularity";
import Seo from "../components/Seo";
import SeoTextBlock from "../components/SeoTextBlock";

const FEATURED_STORAGE_KEY = "up-featured-config";
const PROMO_DISMISS_KEY = "up-signup-promo-dismissed";
const POPULARITY_STORAGE_KEY = "up-game-popularity-map";
const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://octogames.io").replace(
  /\/+$/,
  ""
);
const BANNER_TYPES = {
  DEFAULT: "default",
  GAME: "game"
};

const normalizeBanner = (value) => {
  if (typeof value === "string") {
    return {
      type: BANNER_TYPES.GAME,
      gameId: value,
      image: "",
      text: "",
      url: ""
    };
  }
  if (!value || typeof value !== "object") return null;

  return {
    type:
      value.type === BANNER_TYPES.DEFAULT ? BANNER_TYPES.DEFAULT : BANNER_TYPES.GAME,
    gameId: typeof value.gameId === "string" ? value.gameId : "",
    image: typeof value.image === "string" ? value.image : "",
    text: typeof value.text === "string" ? value.text : "",
    url: typeof value.url === "string" ? value.url : ""
  };
};

const normalizeFeaturedConfig = (value) => {
  if (!value || typeof value !== "object") {
    return { banners: [], recommended: [] };
  }
  return {
    banners: (Array.isArray(value.banners) ? value.banners : [])
      .map(normalizeBanner)
      .filter(Boolean),
    recommended: (Array.isArray(value.recommended) ? value.recommended : []).filter(
      (id) => typeof id === "string"
    )
  };
};

const isExternalUrl = (url) => /^https?:\/\//i.test(url);

const sortOptions = [
  { value: "popular", label: "By popularity" },
  { value: "added", label: "By date added" },
  { value: "title", label: "By name A–Z" }
];

const NON_FILTER_GENRE_PATTERNS = [
  /^html5$/i,
  /^javascript$/i,
  /^webgl$/i,
  /^cocos$/i,
  /^defold$/i,
  /^gdevelop$/i,
  /^melonjs$/i,
  /^pixijs$/i,
  /^construct\s*\d*$/i,
  /^phaser\s*\d*$/i
];

const isRelevantFilterGenre = (genre) =>
  typeof genre === "string" &&
  genre.trim().length > 0 &&
  !NON_FILTER_GENRE_PATTERNS.some((pattern) => pattern.test(genre.trim()));

/** URL slug for genre (e.g. "Action" -> "action", ".IO" -> "io") */
const categoryToSlug = (cat) => {
  if (!cat || cat === "All genres") return "";
  return cat
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/\./g, "")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

/** Resolve URL slug back to category display name */
const slugToCategory = (slug, categoryList) => {
  if (!slug || !categoryList || !categoryList.length) return "All genres";
  const lower = slug.toLowerCase().trim();
  for (const cat of categoryList) {
    if (cat === "All genres") continue;
    if (categoryToSlug(cat) === lower) return cat;
  }
  return "All genres";
};

const Home = () => {
  const navigate = useNavigate();
  const { genreSlug } = useParams();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [featured, setFeatured] = useState({ banners: [], recommended: [] });
  const [featuredLoaded, setFeaturedLoaded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [heroImagesReady, setHeroImagesReady] = useState(false);
  const [popularityLoaded, setPopularityLoaded] = useState(false);
  const [popularityByGameId, setPopularityByGameId] = useState({});
  const [mobileOnly, setMobileOnly] = useState(false);
  const heroContainerRef = useRef(null);
  const heroCardRefs = useRef([]);
  const gridRef = useRef(null);

  const categories = useMemo(() => {
    const genreSet = new Set();
    games.forEach((g) => {
      if (Array.isArray(g.genres)) {
        g.genres.forEach((genre) => {
          if (isRelevantFilterGenre(genre)) {
            genreSet.add(genre);
          }
        });
      }
    });
    const sortedGenres = Array.from(genreSet).sort((a, b) =>
      a.localeCompare(b, "en")
    );
    return ["All genres", ...sortedGenres];
  }, []);

  const category = useMemo(
    () => slugToCategory(genreSlug || "", categories),
    [genreSlug, categories]
  );

  useEffect(() => {
    if (genreSlug && category === "All genres") {
      navigate("/", { replace: true });
    }
  }, [genreSlug, category, navigate]);

  const gameById = useMemo(() => {
    const map = new Map();
    games.forEach((g) => {
      map.set(g.id, g);
    });
    return map;
  }, []);

  const originalOrderById = useMemo(() => {
    const map = new Map();
    games.forEach((g, index) => {
      map.set(g.id, index);
    });
    return map;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = window.localStorage.getItem(PROMO_DISMISS_KEY) === "1";
    setPromoDismissed(dismissed);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);
      if (!user) {
        setFavoriteIds(new Set());
        return;
      }
      try {
        const favorites = await getFavoritesForUser(user.uid);
        setFavoriteIds(new Set(favorites.map((f) => f.id)));
      } catch {
        // ignore loading favorites errors
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(POPULARITY_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            setPopularityByGameId(parsed);
          }
        }
      } catch {
        // ignore cache parse failures
      }
    }

    const loadPopularity = async () => {
      try {
        const map = await getPopularityMap();
        if (!cancelled) {
          setPopularityByGameId(map);
          if (typeof window !== "undefined") {
            try {
              window.localStorage.setItem(POPULARITY_STORAGE_KEY, JSON.stringify(map));
            } catch {
              // ignore cache write failures
            }
          }
        }
      } catch {
        // ignore popularity fetch failures in UI
      } finally {
        if (!cancelled) {
          setPopularityLoaded(true);
        }
      }
    };

    loadPopularity();

    return () => {
      cancelled = true;
    };
  }, []);

  // load featured configuration from Firestore (shared for all users)
  useEffect(() => {
    const featuredRef = doc(db, "siteConfig", "featured");
    const unsubscribe = onSnapshot(
      featuredRef,
      (snap) => {
        if (snap.exists()) {
          const normalized = normalizeFeaturedConfig(snap.data());
          setFeatured(normalized);
          setFeaturedLoaded(true);

          // keep local cache so admin browser and offline cases stay usable
          if (typeof window !== "undefined") {
            window.localStorage.setItem(FEATURED_STORAGE_KEY, JSON.stringify(normalized));
          }
          return;
        }

        // If document is missing, keep defaults/fallback behavior.
        setFeatured({ banners: [], recommended: [] });
        setFeaturedLoaded(true);
      },
      (err) => {
        // Make failures explicit so production issues aren't silent.
        // eslint-disable-next-line no-console
        console.error("Failed to read featured config from Firestore:", err);

        // fallback: try localStorage cache
        if (typeof window !== "undefined") {
          try {
            const raw = window.localStorage.getItem(FEATURED_STORAGE_KEY);
            if (raw) {
              setFeatured(normalizeFeaturedConfig(JSON.parse(raw)));
            }
          } catch {
            // ignore
          }
        }
        setFeaturedLoaded(true);
      }
    );

    return () => unsubscribe();
  }, []);

  const heroItems = useMemo(() => {
    if (!featuredLoaded) return [];

    const fromConfig = (featured.banners || [])
      .map((banner, index) => {
        if (banner.type === BANNER_TYPES.DEFAULT) {
          const image = banner.image.trim();
          const text = banner.text.trim();
          const href = banner.url.trim();
          if (!image || !text || !href) return null;
          return {
            id: `default-${index}`,
            image,
            text,
            href,
            external: isExternalUrl(href),
            ctaLabel: "Open"
          };
        }

        const linkedGame = banner.gameId ? gameById.get(banner.gameId) : null;
        if (!linkedGame) return null;
        const image = banner.image.trim() || linkedGame.thumbnail;
        if (!image) return null;
        const text = banner.text.trim() || linkedGame.title;
        return {
          id: `game-${linkedGame.id}-${index}`,
          image,
          text,
          href: `/game/${linkedGame.slug}`,
          external: false,
          ctaLabel: "Play"
        };
      })
      .filter(Boolean);

    if (fromConfig.length > 0) return fromConfig;

    return games.slice(0, 3).map((game, index) => ({
      id: `fallback-${game.id}-${index}`,
      image: game.thumbnail,
      text: game.title,
      href: `/game/${game.slug}`,
      external: false,
      ctaLabel: "Play"
    }));
  }, [featuredLoaded, featured.banners, gameById]);

  const loopedHeroItems = useMemo(() => {
    if (heroItems.length <= 1) return heroItems;
    return [...heroItems, ...heroItems];
  }, [heroItems]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setHeroImagesReady(true);
      return;
    }

    if (!featuredLoaded) {
      setHeroImagesReady(false);
      return;
    }

    const urls = heroItems.map((item) => item.image).filter(Boolean);
    if (!urls.length) {
      setHeroImagesReady(true);
      return;
    }

    let isCancelled = false;
    heroCardRefs.current = [];
    setHeroImagesReady(false);

    const loadImage = (src) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
      });

    Promise.all(urls.map(loadImage)).then(() => {
      if (!isCancelled) {
        setHeroImagesReady(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [featuredLoaded, heroItems]);

  // recommended strip under banner
  const recommendedGames = useMemo(() => {
    if (!featuredLoaded) return [];

    if (featured.recommended && featured.recommended.length > 0) {
      return featured.recommended
        .map((id) => gameById.get(id))
        .filter(Boolean);
    }
    return games.slice(3, 12);
  }, [featuredLoaded, featured.recommended, gameById]);

  const gamesReady = sort !== "popular" || popularityLoaded;

  const homeJsonLd = useMemo(
    () => {
      const isGenrePage = category !== "All genres";
      const pagePath = isGenrePage ? `/genre/${categoryToSlug(category)}` : "/";
      const pageName = isGenrePage ? `${category} Games` : "Play Free Online Games";
      const pageDescription = isGenrePage
        ? `Play free ${category} games online at OctoGames. No install, no download.`
        : "Play free browser games instantly on OctoGames. Discover new releases, featured banners, and recommended titles.";
      return [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "OctoGames",
          url: SITE_URL,
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: pageName,
          url: `${SITE_URL}${pagePath}`,
          description: pageDescription
        }
      ];
    },
    [category]
  );

  const filtered = useMemo(() => {
    if (!gamesReady) return [];

    let list = [...games];

    if (category !== "All genres") {
      list = list.filter(
        (g) => Array.isArray(g.genres) && g.genres.includes(category)
      );
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (g) =>
          g.title.toLowerCase().includes(q) ||
          (g.description || "").toLowerCase().includes(q)
      );
    }

    if (mobileOnly) {
      list = list.filter((g) => {
        const w = Number(g.width);
        const h = Number(g.height);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
          return false;
        }
        return w < h;
      });
    }

    if (sort === "title") {
      list.sort((a, b) => a.title.localeCompare(b.title, "en"));
    } else if (sort === "popular") {
      list.sort((a, b) => {
        const clicksA = popularityByGameId[a.id] || 0;
        const clicksB = popularityByGameId[b.id] || 0;
        if (clicksA !== clicksB) {
          return clicksB - clicksA;
        }

        // Keep zero-click and equal-click games in current/default order.
        return (originalOrderById.get(a.id) || 0) - (originalOrderById.get(b.id) || 0);
      });
    }
    // "added" keeps original order.

    return list;
  }, [gamesReady, category, search, sort, mobileOnly, popularityByGameId, originalOrderById]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const pageGames = useMemo(() => {
    const size = pageSize || 1;
    const start = (page - 1) * size;
    return filtered.slice(start, start + size);
  }, [filtered, page, pageSize]);

  // adjust page size: 5 rows on desktop, as many columns as fit; rows stay full
  useEffect(() => {
    const CARD_SIZE = 180;
    const GAP = 6;

    const computePageSize = () => {
      if (typeof window === "undefined") return;
      const grid = gridRef.current;
      const width = grid?.clientWidth || window.innerWidth || 1280;

      const isMobile = width < 640;
      const isDesktop = width >= 769;

      let columns = 1;
      if (isDesktop) {
        columns = Math.max(1, Math.floor((width + GAP) / (CARD_SIZE + GAP)));
      } else if (grid && !isMobile) {
        const template = window.getComputedStyle(grid).gridTemplateColumns || "";
        const parsed = template
          .split(" ")
          .map((part) => part.trim())
          .filter(Boolean)
          .length;
        if (parsed > 0) columns = parsed;
      } else if (isMobile) {
        columns = 2;
      }

      const rows = isMobile ? 6 : 5;

      setPageSize(columns * rows);
    };

    computePageSize();
    if (typeof window !== "undefined") {
      const id = window.setTimeout(computePageSize, 0);
      window.addEventListener("resize", computePageSize);
      return () => {
        window.clearTimeout(id);
        window.removeEventListener("resize", computePageSize);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    setPage(1);
  }, [category, search, sort]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // scroll to top of page (show hero banners) when page changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // auto-scroll hero carousel every 2s with seamless loop
  useEffect(() => {
    if (!heroImagesReady) return undefined;
    if (heroItems.length <= 1) return undefined;
    const container = heroContainerRef.current;
    if (!container) return undefined;

    let resetTimeoutId;
    let index = 0;

    const interval = setInterval(() => {
      if (!heroCardRefs.current.length) return;
      index += 1;

      const card = heroCardRefs.current[index];
      if (card && container) {
        container.scrollTo({
          left: card.offsetLeft,
          behavior: "smooth"
        });
      }

      if (index >= heroItems.length) {
        // Jump back to the original set after reaching duplicate set
        resetTimeoutId = window.setTimeout(() => {
          const firstCard = heroCardRefs.current[0];
          if (firstCard && container) {
            container.scrollTo({
              left: firstCard.offsetLeft,
              behavior: "auto"
            });
          }
          index = 0;
        }, 450);
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      if (resetTimeoutId) window.clearTimeout(resetTimeoutId);
    };
  }, [heroImagesReady, heroItems, loopedHeroItems.length]);

  const handleToggleFavorite = async (e, game) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      navigate("/account?mode=signin");
      return;
    }

    try {
      const updated = await toggleFavoriteForUser(currentUser.uid, game);
      setFavoriteIds(new Set(updated.map((f) => f.id)));
    } catch {
      // ignore toggle failures in UI
    }
  };

  const handleDismissPromo = () => {
    setPromoDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PROMO_DISMISS_KEY, "1");
    }
  };

  return (
    <div className="up-page">
      <Seo
        title={category === "All genres" ? "Play Free Online Games" : `${category} Games`}
        description={
          category === "All genres"
            ? "Play free browser games instantly on OctoGames. Explore featured banners, recommended titles, and hundreds of playable games."
            : `Play free ${category} games online at OctoGames. No install, no download. Browser games you can play instantly.`
        }
        path={category === "All genres" ? "/" : `/genre/${categoryToSlug(category)}`}
        keywords={
          category === "All genres"
            ? "online games, browser games, free games, html5 games, play games now"
            : `${category.toLowerCase()} games, free ${category.toLowerCase()} games, browser games, online games`
        }
        jsonLd={homeJsonLd}
      />
      {!currentUser && !promoDismissed && (
        <section className="container-wide up-signup-promo-wrap">
          <div className="up-signup-promo">
            <p className="up-signup-promo-text">
              Unlock your full potential with OctoGames -{" "}
              <Link to="/account?mode=signup" className="up-signup-promo-link">
                Sign up!
              </Link>
            </p>
            <button
              type="button"
              className="up-signup-promo-close"
              onClick={handleDismissPromo}
              aria-label="Close signup banner"
            >
              ×
            </button>
          </div>
        </section>
      )}

      {/* HERO CAROUSEL */}
      <section className="up-hero container-wide">
        {!heroImagesReady ? (
          <div className="up-hero-row up-hero-row-skeleton" aria-hidden="true">
            {[0, 1, 2].map((idx) => (
              <div key={idx} className="up-hero-card up-hero-card-skeleton">
                <div className="up-hero-skeleton-shimmer" />
              </div>
            ))}
          </div>
        ) : (
          <div className="up-hero-row" ref={heroContainerRef}>
            {loopedHeroItems.map((item, index) => {
              const cardContent = (
                <>
                  <img src={item.image} alt={item.text || "Banner"} className="up-hero-img" />
                  <div className="up-hero-overlay" />
                  <div className="up-hero-play-wrap">
                    <span className="up-hero-play-btn">{item.ctaLabel}</span>
                  </div>
                  {item.text && (
                    <div className="up-hero-title-bar">
                      <span className="up-hero-title-text">{item.text}</span>
                    </div>
                  )}
                </>
              );

              if (item.external) {
                return (
                  <a
                    key={`${item.id}-${index}`}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="up-hero-card"
                    ref={(el) => {
                      heroCardRefs.current[index] = el;
                    }}
                  >
                    {cardContent}
                  </a>
                );
              }

              return (
                <Link
                  key={`${item.id}-${index}`}
                  to={item.href}
                  className="up-hero-card"
                  ref={(el) => {
                    heroCardRefs.current[index] = el;
                  }}
                >
                  {cardContent}
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* RECOMMENDED STRIP */}
      <section className="up-section container">
        <header className="up-section-header">
          <h2 className="up-section-title">Recommended</h2>
        </header>
        {!featuredLoaded ? (
          <div className="up-strip" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <div key={idx} className="up-strip-item up-hero-card-skeleton">
                <div className="up-hero-skeleton-shimmer" />
              </div>
            ))}
          </div>
        ) : (
          <div className="up-strip">
            {recommendedGames.map((game) => (
              <Link
                key={game.id}
                to={`/game/${game.slug}`}
                className="up-strip-item"
              >
                {game.thumbnail && (
                  <img
                    src={game.thumbnail}
                    alt={game.title}
                    className="up-strip-img"
                  />
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* AVAILABLE GAMES */}
      <section className="up-section container">
        <header
          className={
            "up-section-header up-available-header" +
            (filtersOpen ? " filters-open" : "")
          }
        >
          <div className="up-available-title-row">
            <h2 className="up-section-title">Available games</h2>
            <button
              type="button"
              className="up-filter-toggle"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters
            </button>
          </div>
          <div className="up-available-controls">
            <div className="up-sort-menu">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={
                    "up-sort-pill" +
                    (sort === opt.value ? " up-sort-pill-active" : "")
                  }
                  onClick={() => setSort(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="up-search-wrap">
              <span className="up-search-icon">🔍</span>
              <input
                className="up-search-input"
                placeholder="Search games"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className={
                "pill up-mobile-filter-pill" +
                (mobileOnly ? " pill-active up-pill-dark" : "")
              }
              onClick={() => {
                setMobileOnly((v) => !v);
                setPage(1);
              }}
            >
              Mobile only
            </button>
          </div>
        </header>

        <div className="up-categories">
          {categories.map((cat) => {
            const isAll = cat === "All genres";
            const href = isAll ? "/" : `/genre/${categoryToSlug(cat)}`;
            const isActive = category === cat;
            return (
              <Link
                key={cat}
                to={href}
                className={
                  "pill" + (isActive ? " pill-active up-pill-dark" : "")
                }
              >
                {cat}
              </Link>
            );
          })}
        </div>

        <div className="up-games-grid" ref={gridRef}>
          {!gamesReady
            ? Array.from({ length: pageSize || 12 }).map((_, idx) => (
              <div key={`skeleton-${idx}`} className="up-game-card up-hero-card-skeleton" aria-hidden="true">
                <div className="up-hero-skeleton-shimmer" />
              </div>
            ))
            : pageGames.map((game) => (
              <Link
                key={game.id}
                to={`/game/${game.slug}`}
                className="up-game-card"
              >
                <button
                  type="button"
                  className={
                    "up-fav-btn" + (favoriteIds.has(game.id) ? " up-fav-btn-active" : "")
                  }
                  onClick={(e) => handleToggleFavorite(e, game)}
                  aria-label={
                    favoriteIds.has(game.id)
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                  title={
                    favoriteIds.has(game.id)
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                >
                  {favoriteIds.has(game.id) ? "♥" : "♡"}
                </button>
                {game.thumbnail && (
                  <div className="up-game-cover-wrap">
                    <img
                      src={game.thumbnail}
                      alt={game.title}
                      className="up-game-cover"
                    />
                  </div>
                )}
                <div className="up-game-info">
                  <div className="up-game-title" title={game.title}>
                    {game.title}
                  </div>
                </div>
              </Link>
            ))}
        </div>

        {gamesReady && filtered.length === 0 && (
          <p className="empty-state">
            No games match your filters. Try changing the search.
          </p>
        )}

        {gamesReady && totalPages > 1 && (
          <div className="pagination">
            <button
              type="button"
              className="page-nav"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>

            <div className="page-pills">
              {Array.from({ length: totalPages }).map((_, index) => {
                const p = index + 1;
                if (
                  p === 1 ||
                  p === totalPages ||
                  (p >= page - 1 && p <= page + 1)
                ) {
                  return (
                    <button
                      key={p}
                      type="button"
                      className={
                        "page-pill" + (p === page ? " page-pill-active" : "")
                      }
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  );
                }
                if (p === 2 && page > 3) {
                  return (
                    <span key="start-ellipsis" className="page-ellipsis">
                      …
                    </span>
                  );
                }
                if (p === totalPages - 1 && page < totalPages - 2) {
                  return (
                    <span key="end-ellipsis" className="page-ellipsis">
                      …
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              type="button"
              className="page-nav"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <SeoTextBlock
        featuredGames={filtered.slice(0, 12).map((g) => ({ slug: g.slug, title: g.title }))}
        genreLinks={categories
          .filter((c) => c !== "All genres")
          .map((c) => ({ name: c, slug: categoryToSlug(c) }))
          .filter((x) => x.slug)}
        currentGenre={category === "All genres" ? null : category}
      />
    </div>
  );
};

export default Home;